import { FC, useEffect, useRef, useState } from 'react';

import SunshineModal, {
  SunshineModalProps,
} from 'components/_core/SunshineModal';
import { DropZoneEvent } from 'components/Upload/components/DropZone/types';
import { logEvent } from 'features/Analytics/analytics';

import PickerStep from './components/PickerStep';
import SubmitStep from './components/SubmitStep';

const getFileDataUrl = async (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject(reader.error);
    };

    reader.readAsDataURL(file);
  });

interface UploadModalProps extends Omit<SunshineModalProps, 'children'> {
  isUploading: boolean;
  onUpload: (logo: File) => Promise<void>;
  onClose: () => void;
}

const UploadModal: FC<UploadModalProps> = ({
  isOpen,
  isUploading,
  onClose,
  onUpload,
}) => {
  const logo = useRef<File | null>(null);
  const [step, setStep] = useState<'upload' | 'confirmation'>('upload');
  const [hasUploadError, setHasUploadError] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      logo.current = null;
      setStep('upload');
      setHasUploadError(false);
      setLogoUrl(null);
    }
  }, [isOpen]);

  const selectLogo = async (file: File, eventMethod: DropZoneEvent) => {
    logo.current = file;
    const logoDataUrl = await getFileDataUrl(file);
    setLogoUrl(logoDataUrl);
    setStep('confirmation');
    logEvent({ name: 'Logo File Select', properties: { eventMethod } });
  };

  const upload = async () => {
    if (!logo.current) {
      return;
    }

    try {
      await onUpload(logo.current);
    } catch (err) {
      setHasUploadError(true);
    }
  };

  return (
    <SunshineModal isOpen={isOpen} onClose={onClose}>
      <SunshineModal.Header />

      {step === 'upload' ? (
        <PickerStep
          onGoBack={() => onClose && onClose()}
          onSelect={selectLogo}
        />
      ) : null}

      {step === 'confirmation' && logoUrl ? (
        <SubmitStep
          fileName={logo.current?.name ?? ''}
          hasUploadError={hasUploadError}
          isLoading={isUploading}
          logoUrl={logoUrl}
          onGoBack={() => {
            setHasUploadError(false);
            setStep('upload');
          }}
          onSubmit={upload}
        />
      ) : null}
    </SunshineModal>
  );
};

export default UploadModal;
